"use client";

import {useId, useState} from "react";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import {Box, IconButton, Menu, MenuItem, Tooltip} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import {useTheme} from "@mui/material/styles";
import LanguageLabel from "./LanguageLabel";
import type {VideoLanguageOption} from "./videoLanguageOption";

interface LanguageFlagMenuProps {
    /* Languages that appear on at least one video, shown as flag choices. */

    languages: VideoLanguageOption[];
}

// LANGUAGE FLAG MENU
export default function LanguageFlagMenu({languages}: LanguageFlagMenuProps) {
    /**
     * Flag button on the menu bar that sets the language query parameter.
     * Choosing a language records that code. Clearing it removes the parameter.
     */

    const theme = useTheme();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const menuId = useId();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const requestedCode = searchParams.get("language")?.trim() ?? "";
    const selected = languages.find((language) => language.code === requestedCode) ?? null;

    // SELECT LANGUAGE
    const selectLanguage = (code: string) => {
        /**
         * Writes the language query parameter on the current page, or removes it.
         */

        const params = new URLSearchParams(searchParams.toString());
        if (code) {
            params.set("language", code);
        } else {
            params.delete("language");
        }

        const query = params.toString();
        router.push(query ? `${pathname}?${query}` : pathname);
        setAnchorEl(null);
    };

    return (
        <>
            <Tooltip title={selected?.language ?? (requestedCode || "All languages")}>
                <IconButton
                    size="small"
                    aria-label="Language"
                    aria-haspopup="true"
                    aria-controls={anchorEl ? menuId : undefined}
                    aria-expanded={anchorEl ? "true" : undefined}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{p: 0.5}}
                >
                    {selected ? (
                        <Box
                            component="img"
                            src={`/images/flags/${selected.flag}`}
                            alt={selected.language}
                            sx={{
                                width: 28,
                                height: 20,
                                objectFit: "cover",
                                borderRadius: "2px",
                                display: "block",
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        />
                    ) : (
                        <LanguageIcon sx={{color: theme.custom.menuItemColour}} />
                    )}
                </IconButton>
            </Tooltip>
            <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{paper: {sx: {maxHeight: 360}}}}
            >
                <MenuItem selected={requestedCode === ""} onClick={() => selectLanguage("")}>
                    All languages
                </MenuItem>
                {languages.map((language) => (
                    <MenuItem
                        key={language.code}
                        selected={selected?.code === language.code}
                        onClick={() => selectLanguage(language.code)}
                    >
                        <LanguageLabel language={language.language} flag={language.flag} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
